import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";

type ExportObjectType = "data_provider" | "operation" | "page" | "menu" | "semantic_model";
type CatalogNode = { key: string; type: ExportObjectType; id: string; name: string };

type ExportCatalog = {
  dataProviders: Array<{ id: string; name: string }>;
  operations: Array<{ id: string; name: string }>;
  pages: Array<{ id: string; title: string }>;
  menus: Array<{ id: string; name: string }>;
  semanticModels: Array<{ id: string; name: string }>;
  graph: {
    nodes: CatalogNode[];
    downstreamByKey: Record<string, string[]>;
  };
};

type SelectionState = {
  dataProviderIds: string[];
  operationIds: string[];
  pageIds: string[];
  menuIds: string[];
  semanticModelIds: string[];
};

type AtlasExportBundle = {
  manifest: {
    formatVersion: "atlaspkg.v1" | "atlaspkg.v2";
    createdAt: string;
    tenantId: string;
    objectCounts: Record<ExportObjectType, number>;
    rootSelection: SelectionState;
    resolvedSelection: SelectionState;
  };
  payload: {
    dataProviders: unknown[];
    operations: unknown[];
    pages: unknown[];
    menus: unknown[];
    semanticModels: unknown[];
  };
};

type ImportImpactObject = {
  objectType: ExportObjectType;
  sourceObjectKey: string;
  sourceName: string;
  state: "create" | "conflict_name" | "conflict_ref";
  reason?: string;
};
type RequiredRename = {
  objectType: ExportObjectType;
  sourceObjectKey: string;
  sourceName: string;
};
type AnalyzeResponse = {
  objects: ImportImpactObject[];
  summary: { create: number; conflict_name: number; conflict_ref: number; skip: number };
  requiredRenames: RequiredRename[];
};

type WizardStep = 1 | 2 | 3 | 4 | 5;
type CategoryKey = "data_provider" | "operation" | "page" | "menu" | "semantic_model";

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: "data_provider", label: "Data Providers" },
  { key: "operation", label: "Operations" },
  { key: "page", label: "Pages" },
  { key: "menu", label: "Menus" },
  { key: "semantic_model", label: "Semantic Models" }
];

function toSelection(keys: Set<string>): SelectionState {
  const sel: SelectionState = {
    dataProviderIds: [],
    operationIds: [],
    pageIds: [],
    menuIds: [],
    semanticModelIds: []
  };
  for (const key of keys) {
    const [type, id] = key.split(":", 2) as [ExportObjectType, string];
    if (!id) continue;
    if (type === "data_provider") sel.dataProviderIds.push(id);
    if (type === "operation") sel.operationIds.push(id);
    if (type === "page") sel.pageIds.push(id);
    if (type === "menu") sel.menuIds.push(id);
    if (type === "semantic_model") sel.semanticModelIds.push(id);
  }
  return sel;
}

function SummaryPill(props: { label: string; value: number }) {
  return (
    <span className="pill">
      {props.label}: {props.value}
    </span>
  );
}

const chevronStyle: React.CSSProperties = {
  display: "inline-block",
  width: 16,
  fontSize: 10,
  transition: "transform 0.15s",
  userSelect: "none"
};

export function TenantContentTransfer() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<ExportCatalog | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Record<CategoryKey, boolean>>({
    data_provider: true,
    operation: true,
    page: true,
    menu: true,
    semantic_model: true
  });

  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [bundleFileName, setBundleFileName] = useState<string>("");
  const [bundle, setBundle] = useState<AtlasExportBundle | null>(null);
  const [analyzeResult, setAnalyzeResult] = useState<AnalyzeResponse | null>(null);
  const [renameMap, setRenameMap] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<{ status: string } | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        setCatalog(await apiFetch<ExportCatalog>("/api/tenant/content-transfer/catalog"));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upstreamByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!catalog) return map;
    for (const [from, children] of Object.entries(catalog.graph.downstreamByKey)) {
      for (const child of children) {
        const prev = map.get(child) ?? [];
        prev.push(from);
        map.set(child, prev);
      }
    }
    return map;
  }, [catalog]);

  const nodesByKey = useMemo(() => {
    const map = new Map<string, CatalogNode>();
    if (!catalog) return map;
    for (const n of catalog.graph.nodes) map.set(n.key, n);
    return map;
  }, [catalog]);

  const nodesByType = useMemo(() => {
    const out: Record<CategoryKey, CatalogNode[]> = {
      data_provider: [],
      operation: [],
      page: [],
      menu: [],
      semantic_model: []
    };
    for (const n of nodesByKey.values()) out[n.type].push(n);
    return out;
  }, [nodesByKey]);

  function walkTransitive(start: string, lookup: (k: string) => string[]): Set<string> {
    const seen = new Set<string>();
    const stack = [start];
    while (stack.length > 0) {
      const k = stack.pop() as string;
      for (const next of lookup(k)) {
        if (!seen.has(next)) { seen.add(next); stack.push(next); }
      }
    }
    return seen;
  }

  function onToggleItem(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
        for (const dep of walkTransitive(key, (k) => upstreamByKey.get(k) ?? [])) next.add(dep);
      } else {
        next.delete(key);
        for (const dep of walkTransitive(key, (k) => catalog?.graph.downstreamByKey[k] ?? [])) next.delete(dep);
      }
      return next;
    });
  }

  function selectedCountForCategory(cat: CategoryKey): number {
    let n = 0;
    for (const node of nodesByType[cat]) {
      if (selectedKeys.has(node.key)) n++;
    }
    return n;
  }

  async function runExport() {
    setError(null);
    try {
      const res = await apiFetch<{ fileName: string; bundle: AtlasExportBundle }>("/api/tenant/content-transfer/export", {
        method: "POST",
        body: { selection: toSelection(selectedKeys) }
      });
      const blob = new Blob([JSON.stringify(res.bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.push({ kind: "success", message: `Exported ${res.fileName}` });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadBundleFile(file: File) {
    setError(null);
    setBundleFileName(file.name);
    setAnalyzeResult(null);
    setRenameMap({});
    setImportResult(null);
    try {
      const parsed = JSON.parse(await file.text()) as AtlasExportBundle;
      setBundle(parsed);
      setWizardStep(2);
    } catch (e) {
      setBundle(null);
      setWizardStep(1);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function goAnalyzeStep() {
    if (!bundle) return;
    setError(null);
    try {
      const data = await apiFetch<AnalyzeResponse>("/api/tenant/content-transfer/import/analyze", {
        method: "POST",
        body: { bundle }
      });
      setAnalyzeResult(data);
      const initial: Record<string, string> = {};
      for (const row of data.requiredRenames) initial[row.sourceObjectKey] = `${row.sourceName} (imported)`;
      setRenameMap(initial);
      setWizardStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const unresolvedConflicts = useMemo(() => {
    if (!analyzeResult) return 0;
    const names = analyzeResult.requiredRenames.filter((r) => !(renameMap[r.sourceObjectKey] ?? "").trim()).length;
    const refs = analyzeResult.objects.filter((r) => r.state === "conflict_ref").length;
    return names + refs;
  }, [analyzeResult, renameMap]);

  async function applyImportNow() {
    if (!bundle || !analyzeResult) return;
    setError(null);
    try {
      const renamePayload = Object.entries(renameMap)
        .map(([sourceObjectKey, newName]) => {
          const req = analyzeResult.requiredRenames.find((r) => r.sourceObjectKey === sourceObjectKey);
          if (!req) return null;
          return { objectType: req.objectType, sourceObjectKey, newName: newName.trim() };
        })
        .filter((r): r is { objectType: ExportObjectType; sourceObjectKey: string; newName: string } => !!r);
      const res = await apiFetch<{ status: string }>("/api/tenant/content-transfer/import/apply", {
        method: "POST",
        body: { bundle, renameMap: renamePayload, options: { overwriteExisting: false } }
      });
      setImportResult(res);
      setWizardStep(5);
      toast.push({ kind: "success", message: "Import completed." });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (loading) return <LoadingState />;

  return (
    <PageLayout title="Import / Export">
      {error ? <ErrorState detail={error} /> : null}

      {/* ── Export ── */}
      <div className="card" style={{ marginTop: 12, maxWidth: 560 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Export</div>
        <div style={{ border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
          {CATEGORIES.map((cat) => {
            const items = nodesByType[cat.key];
            const selCount = selectedCountForCategory(cat.key);
            const isOpen = !collapsed[cat.key];
            return (
              <div key={cat.key} style={{ borderBottom: "1px solid var(--border)" }}>
                <button
                  type="button"
                  onClick={() => setCollapsed((p) => ({ ...p, [cat.key]: !p[cat.key] }))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    width: "100%",
                    padding: "6px 10px",
                    background: "var(--bg-elevated, var(--bg))",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "inherit",
                    textAlign: "left"
                  }}
                >
                  <span style={{ ...chevronStyle, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>&#9654;</span>
                  <span style={{ flex: 1 }}>{cat.label}</span>
                  {selCount > 0 ? (
                    <span className="pill" style={{ fontSize: 11 }}>{selCount}</span>
                  ) : (
                    <span className="muted" style={{ fontSize: 11 }}>{items.length}</span>
                  )}
                </button>
                {isOpen ? (
                  items.length === 0 ? (
                    <div className="muted" style={{ padding: "4px 10px 8px 32px", fontSize: 12 }}>None</div>
                  ) : (
                    <div style={{ padding: "2px 0 6px" }}>
                      {items.map((item) => {
                        const checked = selectedKeys.has(item.key);
                        return (
                          <label
                            key={item.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "2px 10px 2px 32px",
                              fontSize: 13,
                              cursor: "pointer"
                            }}
                          >
                            <input type="checkbox" checked={checked} onChange={(e) => onToggleItem(item.key, e.target.checked)} />
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )
                ) : null}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
          <button className="btn btnPrimary" type="button" onClick={() => void runExport()} disabled={selectedKeys.size === 0}>
            Export
          </button>
          <span className="muted" style={{ fontSize: 12 }}>{selectedKeys.size} selected</span>
        </div>
      </div>

      {/* ── Import Wizard ── */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Import</div>

        {wizardStep === 1 ? (
          <label className="field" style={{ maxWidth: 400 }}>
            <span className="label">Upload .atlaspkg.json</span>
            <input
              className="input"
              type="file"
              accept=".json,.atlaspkg.json,application/json"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void loadBundleFile(f); }}
            />
          </label>
        ) : null}

        {wizardStep >= 2 && bundle ? (
          <div className="card" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Review</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {bundleFileName} &middot; {bundle.manifest.formatVersion} &middot; {bundle.manifest.createdAt}
            </div>
            <div className="toolbar" style={{ marginTop: 6 }}>
              <SummaryPill label="Providers" value={bundle.manifest.objectCounts.data_provider ?? 0} />
              <SummaryPill label="Ops" value={bundle.manifest.objectCounts.operation ?? 0} />
              <SummaryPill label="Pages" value={bundle.manifest.objectCounts.page ?? 0} />
              <SummaryPill label="Menus" value={bundle.manifest.objectCounts.menu ?? 0} />
              <SummaryPill label="Semantic" value={bundle.manifest.objectCounts.semantic_model ?? 0} />
            </div>
            {wizardStep === 2 ? (
              <div className="actions" style={{ marginTop: 8 }}>
                <button className="btn" type="button" onClick={() => setWizardStep(1)}>Back</button>
                <button className="btn btnPrimary" type="button" onClick={() => void goAnalyzeStep()}>Continue</button>
              </div>
            ) : null}
          </div>
        ) : null}

        {wizardStep >= 3 && analyzeResult ? (
          <div className="card" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Impact Analysis</div>
            <div className="toolbar" style={{ marginTop: 6 }}>
              <SummaryPill label="Create" value={analyzeResult.summary.create} />
              <SummaryPill label="Name conflicts" value={analyzeResult.summary.conflict_name} />
              <SummaryPill label="Ref conflicts" value={analyzeResult.summary.conflict_ref} />
            </div>
            <div className="tableWrap" style={{ marginTop: 6 }}>
              <table className="table">
                <thead>
                  <tr><th>Type</th><th>Name</th><th>State</th><th>Rename</th></tr>
                </thead>
                <tbody>
                  {analyzeResult.objects.map((row) => {
                    const needsRename = analyzeResult.requiredRenames.some((r) => r.sourceObjectKey === row.sourceObjectKey);
                    return (
                      <tr key={row.sourceObjectKey}>
                        <td>{row.objectType}</td>
                        <td>{row.sourceName}</td>
                        <td><span className="pill">{row.state}</span></td>
                        <td>
                          {needsRename ? (
                            <input
                              className="input"
                              style={{ minWidth: 160, fontSize: 12, padding: "2px 6px" }}
                              value={renameMap[row.sourceObjectKey] ?? ""}
                              onChange={(e) => setRenameMap((p) => ({ ...p, [row.sourceObjectKey]: e.target.value }))}
                              placeholder="New name"
                            />
                          ) : (
                            <span className="muted">{row.reason ?? "—"}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {wizardStep === 3 ? (
              <div className="actions" style={{ marginTop: 8 }}>
                <button className="btn" type="button" onClick={() => setWizardStep(2)}>Back</button>
                <button className="btn btnPrimary" type="button" disabled={unresolvedConflicts > 0} onClick={() => setWizardStep(4)}>
                  Continue
                </button>
                {unresolvedConflicts > 0 ? <span className="muted">Resolve all conflicts first.</span> : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {wizardStep === 4 ? (
          <div className="card" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Confirm</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Ready to apply import to this tenant.</div>
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn" type="button" onClick={() => setWizardStep(3)}>Back</button>
              <button className="btn btnPrimary" type="button" onClick={() => void applyImportNow()}>Apply import</button>
            </div>
          </div>
        ) : null}

        {wizardStep === 5 ? (
          <div className="card" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Complete</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Import finished: <span className="pill">{importResult?.status ?? "unknown"}</span>
            </div>
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn" type="button" onClick={() => setWizardStep(1)}>Import another</button>
            </div>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
